/**
 * Unified Logging Utility
 * 
 * Centralized logging system following best practices:
 * - Structured logging with JSON format
 * - Log levels: DEBUG, INFO, WARN, ERROR
 * - Entity change tracking with associations
 * - Security event logging (login, logout, failed attempts, deletions)
 * - Async batch operations
 * - No sensitive data logging
 * - Human-readable document IDs
 */

import { db, auth } from './firebase';
import { getDoc, doc as docRef, setDoc, serverTimestamp, Timestamp } from 'firebase/firestore';
import { collectionPath } from './firestoreCollections';
import { formatDate } from './dateHelpers';
import { generateLogId, LOG_LEVELS, generateSecurityLogId, SECURITY_ACTIONS } from './documentIdUtils';

// Re-export for convenience
export { SECURITY_ACTIONS } from './documentIdUtils';

/**
 * Get user name by ID (helper for resolving user references)
 */
export function getUserNameById(users, uid) {
  if (!uid) return '';
  const user = users?.find(u => u.uid === uid);
  return (
    user?.displayName ||
    user?.name ||
    `${user?.firstName || ''} ${user?.lastName || ''}`.trim() ||
    user?.email ||
    uid
  );
}

/**
 * Normalize values for comparison (arrays, timestamps, objects, primitives)
 */
function normalizeForCompare(val) {
  if (val === undefined || val === null) return null;
  // Firestore Timestamp
  if (val && typeof val.toDate === 'function') return val.toDate().toISOString();
  // Arrays -> normalized sorted array of trimmed strings
  if (Array.isArray(val)) {
    return val
      .map(v => (v === null || v === undefined ? '' : String(v).trim()))
      .filter(v => v !== '')
      .sort();
  }
  // Plain object -> normalized object with sorted keys
  if (typeof val === 'object') {
    const keys = Object.keys(val).sort();
    const o = {};
    for (const k of keys) {
      o[k] = normalizeForCompare(val[k]);
    }
    return o;
  }
  // Primitive -> trimmed string
  return String(val).trim();
}

/**
 * Format values for log display
 */
function formatForLog(val) {
  if (val === undefined || val === null) return '';
  // Use formatDate for Timestamps to show date only
  if (val && typeof val.toDate === 'function') return formatDate(val.toDate(), 'MM/DD/YYYY');
  if (Array.isArray(val)) {
    // Handle arrays: if objects inside, show count; if primitives, join them
    if (val.length === 0) return '[]';
    if (typeof val[0] === 'object' && val[0] !== null) {
      return `[${val.length} item${val.length !== 1 ? 's' : ''}]`;
    }
    return val.join(', ');
  }
  if (typeof val === 'object') return JSON.stringify(val);
  return String(val);
}

/**
 * Get collection name for entity type
 */
function getCollectionName(entityType) {
  if (!entityType) return collectionPath('logs');
  const map = {
    customer: collectionPath('logsCustomer'),
    Customer: collectionPath('logsCustomer'), // Uppercase variant
    Opportunity: collectionPath('logsOpp'),
    Contact: collectionPath('logsContact'),
    Task: collectionPath('logsTask'),
    Order: collectionPath('logsOrder'),
    User: collectionPath('logsUser'),
    Role: collectionPath('logsUser'), // Role definition changes routed to user logs (IAM audit trail)
    Addon: collectionPath('logsUser'), // Account-wide add-on toggles (admin audit trail)
    Recommendation: collectionPath('logsCustomer'), // AI Sales Agent decisions show in the customer's Activity Log
    DecisionBrief: collectionPath('logsCustomer'),
    Quote: collectionPath('logsCustomer'),
    QuoteSettings: collectionPath('logs'),
    Security: collectionPath('securityLogs'),
    Email: collectionPath('logsEmail'),
    EmailCampaign: collectionPath('logsEmail'),
    EmailTemplate: collectionPath('logsEmail'),
    EmailSegment: collectionPath('logsEmail'),
    ChatbotKnowledge: collectionPath('logsChatbot'),
    ChatFeedback: collectionPath('logsChatbot'),
    ChatSession: collectionPath('logsChatbot'),
    Knowledge: collectionPath('logsKnowledge'),
    Faq: collectionPath('logsKnowledge'),
    ReportSubscription: collectionPath('logsReports'),
    DataSync: collectionPath('logsDataSync'),
    CalendarEvent: collectionPath('logsCalendar')
  };
  return map[entityType] || collectionPath('logs');
}

/**
 * Sanitize sensitive data from log payload
 * NEVER log: passwords, API keys, tokens, credit cards, SSNs
 */
function sanitizeLogPayload(payload) {
  const sensitiveFields = ['password', 'apiKey', 'token', 'secret', 'creditCard', 'ssn', 'refreshToken', 'accessToken'];
  const sanitized = { ...payload };
  
  for (const field of sensitiveFields) {
    if (field in sanitized) {
      sanitized[field] = '[REDACTED]';
    }
  }
  
  return sanitized;
}

/**
 * Log entity changes with change detection
 * 
 * @param {Object} options - Logging options
 * @param {Object} options.oldObj - Previous state (null for creates)
 * @param {Object} options.newObj - New state (null for deletes)
 * @param {Array<string>} options.fields - Fields to track
 * @param {string} options.entityType - Entity type (Customer, Opportunity, Task, etc.)
 * @param {string} options.entityId - Entity ID
 * @param {Object} options.user - Current user object
 * @param {string} options.action - Action performed (Created, Updated, Deleted)
 * @param {Array} options.users - Optional: User list for name resolution
 * @param {string} options.collectionName - Optional: Override collection name
 * @param {Object} options.fieldMap - Optional: Custom field formatters
 * @param {string} options.level - Log level (debug, info, warn, error)
 * @returns {Promise<Object|null>} Log document or null on error
 */
export async function logEntityChanges({
  oldObj = {},
  newObj = {},
  fields = [],
  entityType,
  entityId,
  user,
  action,
  users = [],
  collectionName,
  fieldMap = {},
  level = LOG_LEVELS.INFO,
  extraDetails = null
}) {
  try {
    const changes = [];
    
    // Detect changes
    for (const field of fields) {
      const oldVal = oldObj ? oldObj[field] : undefined;
      const newVal = newObj ? newObj[field] : undefined;

      // Special handling for uid: resolve names
      if (field === 'uid') {
        let oldName = '';
        let newName = '';
        if (users && users.length > 0) {
          oldName = getUserNameById(users, oldVal);
          newName = getUserNameById(users, newVal);
        } else {
          // Fallback: try to fetch from Firestore
          if (oldVal) {
            const snap = await getDoc(docRef(db, collectionPath('users', oldVal)));
            if (snap.exists()) oldName = snap.data().displayName || snap.data().name || snap.data().email || oldVal;
          }
          if (newVal) {
            const snap = await getDoc(docRef(db, collectionPath('users', newVal)));
            if (snap.exists()) newName = snap.data().displayName || snap.data().name || snap.data().email || newVal;
          }
        }
        if (oldVal !== newVal) {
          changes.push({
            field: 'Assigned To',
            old: oldName,
            new: newName
          });
        }
        continue;
      }

      // Use fieldMap if provided
      let displayOld = oldVal;
      let displayNew = newVal;
      if (fieldMap && typeof fieldMap[field] === 'function') {
        displayOld = fieldMap[field](oldVal);
        displayNew = fieldMap[field](newVal);
      }

      // Default comparison
      const nOld = normalizeForCompare(oldVal);
      const nNew = normalizeForCompare(newVal);
      const oldStr = JSON.stringify(nOld);
      const newStr = JSON.stringify(nNew);

      if (oldStr !== newStr) {
        changes.push({
          field,
          old: displayOld,
          new: displayNew
        });
      }
    }

    if (changes.length === 0) {
      return null; // Nothing to log
    }

    const changeDetails = changes
      .map(c => `${c.field}: "${formatForLog(c.old)}" → "${formatForLog(c.new)}"`)
      .join('; ');

    // Append caller-supplied context if provided (e.g. "Permission X granted to user Y")
    const details = extraDetails
      ? `${changeDetails} — ${extraDetails}`
      : changeDetails;

    // Entity-specific keys
    // For User entityType, use 'targetUserId' to avoid conflict with 'userId' (the actor)
    const lowerCamelKey = entityType === 'User' 
      ? 'targetUserId' 
      : (entityType ? `${entityType.charAt(0).toLowerCase()}${entityType.slice(1)}Id` : 'entityId');
    const pascalKey = entityType === 'User'
      ? 'TargetUserId'
      : (entityType ? `${entityType}Id` : 'entityId');
    const entitySpecific = { [lowerCamelKey]: entityId, [pascalKey]: entityId };

    // Get related IDs for cross-entity queries
    const customerId = newObj?.customerId || oldObj?.customerId || null;
    const taskNumber = newObj?.taskNumber || oldObj?.taskNumber || null;
    const taskNumberLabel = newObj?.taskNumberLabel || oldObj?.taskNumberLabel || (taskNumber ? `TSK-${taskNumber}` : null);
    const taskId = (entityType === 'Task' && entityId) ? entityId : (newObj?.taskId || oldObj?.taskId || null);
    const opportunityId = (entityType === 'Opportunity' && entityId) ? entityId : (newObj?.opportunityId || oldObj?.opportunityId || null);
    const contactId = (entityType === 'Contact' && entityId) ? entityId : (newObj?.contactId || oldObj?.contactId || null);
    const orderId = (entityType === 'Order' && entityId) ? entityId : (newObj?.orderId || oldObj?.orderId || null);

    // Build associations object for cross-entity queries
    const associations = {
      customerId: customerId || (entityType === 'customer' || entityType === 'Customer' ? entityId : null),
      opportunityId,
      taskId,
      contactId,
      orderId,
      userId: user?.uid || null
    };

    const userName = user
      ? ((user.firstName && user.lastName) ? `${user.firstName} ${user.lastName}` : (user.displayName || user.email || user.uid || null))
      : null;

    // Build log payload
    // CRITICAL FIELD DEFINITIONS:
    // - userId: UID of the person PERFORMING the action (actor/logged-in user)
    // - userName: Display name of the person PERFORMING the action
    // - userEmail: Email of the person PERFORMING the action
    // - entityType: Type of entity being changed (Customer, Opportunity, Task, User, etc.)
    // - entityId: ID of the entity being changed (the target/subject)
    // - targetUserId: (User logs only) UID of the user being changed (when entityType='User')
    // - TargetUserId: (User logs only) Same as targetUserId (for compatibility)
    // - customerId/opportunityId/etc: Entity-specific IDs (e.g., customerId when entityType='Customer')
    let logPayload = {
      userId: user?.uid || 'unknown',
      userEmail: user?.email || null,
      userName,
      action: action || 'Updated',
      details,
      entityType,
      entityId,
      associations, // NEW: Cross-entity associations for unified queries
      customerId,
      taskNumber,
      taskNumberLabel,
      opportunityId,
      level,
      ...entitySpecific,
      timestamp: Timestamp.fromDate(new Date())
    };

    // Sanitize sensitive data
    logPayload = sanitizeLogPayload(logPayload);

    // Determine collection
    const finalCollectionName = collectionName || getCollectionName(entityType);

    // Generate human-readable document ID
    const docId = generateLogId(entityType, entityId, action, level);

    // Write to Firestore using setDoc with generated document ID
    await setDoc(
      docRef(db, finalCollectionName, docId),
      { ...logPayload, timestamp: serverTimestamp() }
    );

    return { ...logPayload, id: docId };
  } catch (err) {
    console.error('logEntityChanges failed:', {
      action: 'LOG_ENTITY_CHANGES',
      error: err.message,
      code: err.code,
      entityType,
      entityId,
      timestamp: new Date().toISOString()
    });
    return null;
  }
}

// ---------------------------------------------------------------------------
// Module-level display-name cache.
// auth.currentUser.displayName is the Firebase Auth profile name and is almost
// never populated — users are created server-side and updateProfile() is not
// called. This cache holds the resolved CRM display name (from the Firestore
// users doc) so we only pay one read per uid per page session.
// ---------------------------------------------------------------------------
const _displayNameCache = new Map(); // uid → string

async function _resolveDisplayName(uid) {
  if (!uid) return null;
  if (_displayNameCache.has(uid)) return _displayNameCache.get(uid);
  try {
    const snap = await getDoc(docRef(db, collectionPath('users', uid)));
    if (snap.exists()) {
      const d = snap.data() || {};
      const name =
        d.displayName ||
        ((d.firstName && d.lastName) ? `${d.firstName} ${d.lastName}`.trim() : null) ||
        d.name ||
        null;
      if (name) {
        _displayNameCache.set(uid, name);
        return name;
      }
    }
  } catch (e) {
    // Non-fatal — fall back to email in caller
  }
  return null;
}

/**
 * Log user action (simplified interface for direct actions)
 * 
 * @param {Object} payload - Log payload
 * @param {string} payload.action - Action performed
 * @param {string} payload.details - Action details
 * @param {string} payload.entityType - Optional: Entity type
 * @param {string} payload.entityId - Optional: Entity ID
 * @param {string} payload.customerId - Optional: Customer ID
 * @param {string} payload.level - Optional: Log level (defaults to INFO)
 * @param {string} collectionName - Optional: Override collection name
 * @returns {Promise<void>}
 */
export async function logUserAction(payload = {}, collectionName) {
  try {
    const currentUid = auth?.currentUser?.uid || payload.userId || 'system';
    const currentEmail = auth?.currentUser?.email || payload.userEmail || null;

    // auth.currentUser.displayName is the Firebase Auth profile name — usually null unless
    // explicitly set via updateProfile(). Prefer the Firestore user doc name instead.
    // Use a module-level cache so we only do one Firestore read per unique uid per session.
    let currentDisplayName = payload.userName || auth?.currentUser?.displayName || null;
    if (!currentDisplayName && currentUid && currentUid !== 'system') {
      currentDisplayName = await _resolveDisplayName(currentUid);
    }

    let docToWrite = {
      ...payload,
      userId: currentUid,
      uid: currentUid,
      userEmail: payload.userEmail || currentEmail,
      userName: payload.userName || currentDisplayName || currentEmail || null,
      level: payload.level || LOG_LEVELS.INFO,
      timestamp: serverTimestamp(),
      createdAt: serverTimestamp()
    };

    // Try to resolve customerId if not provided
    let customerId = docToWrite.customerId || null;
    if (!customerId && docToWrite.taskId) {
      try {
        const taskSnap = await getDoc(docRef(db, collectionPath('tasks', docToWrite.taskId)));
        if (taskSnap.exists()) {
          const t = taskSnap.data();
          customerId = t?.customerId || null;
        }
      } catch (e) {
        console.warn('Failed to load task for customerId fallback:', {
          taskId: docToWrite.taskId,
          error: e.message
        });
      }
    }

    // Set safe sentinel if still null
    if (customerId == null) {
      customerId = 'unassigned';
    }
    docToWrite.customerId = String(customerId);

    // Normalize taskNumber
    if (docToWrite.taskNumber == null && docToWrite.taskNumberLabel) {
      const m = docToWrite.taskNumberLabel.match(/\d+/);
      if (m) docToWrite.taskNumber = Number(m[0]);
    }

    // Build associations object for cross-entity queries (if not already provided)
    if (!docToWrite.associations) {
      const entityType = payload.entityType || '';
      docToWrite.associations = {
        customerId: customerId !== 'unassigned' ? customerId : null,
        opportunityId: entityType === 'Opportunity' ? (payload.entityId || null) : (docToWrite.opportunityId || null),
        taskId: entityType === 'Task' ? (payload.entityId || null) : (docToWrite.taskId || null),
        contactId: entityType === 'Contact' ? (payload.entityId || null) : (docToWrite.contactId || null),
        orderId: entityType === 'Order' ? (payload.entityId || null) : (docToWrite.orderId || null),
        userId: currentUid,
      };
    }

    // Ensure required fields
    docToWrite.action = docToWrite.action || 'Action';
    docToWrite.details = docToWrite.details || '';

    // Sanitize sensitive data
    docToWrite = sanitizeLogPayload(docToWrite);

    // Determine collection
    let finalCollectionName;
    if (collectionName) {
      finalCollectionName = collectionName.includes('/') ? collectionName : collectionPath(collectionName);
    } else {
      finalCollectionName = getCollectionName(payload.entityType);
    }

    // Generate human-readable document ID
    const entityId = payload.entityId || payload.taskId || payload.customerId || 'unknown';
    const docId = generateLogId(
      payload.entityType || 'action',
      entityId,
      payload.action || 'action',
      payload.level || LOG_LEVELS.INFO
    );

    // Write to Firestore using setDoc with generated document ID
    await setDoc(docRef(db, finalCollectionName, docId), docToWrite);
  } catch (err) {
    console.error('logUserAction failed:', {
      action: 'LOG_USER_ACTION',
      error: err.message,
      code: err.code,
      originalAction: payload.action,
      timestamp: new Date().toISOString()
    });
  }
}

/**
 * Batch log multiple entity changes (performance optimization)
 * 
 * @param {Array<Object>} logs - Array of log configurations (same as logEntityChanges params)
 * @returns {Promise<Array<Object|null>>} Array of log results
 */
export async function batchLogEntityChanges(logs) {
  try {
    const logPromises = logs.map(logConfig => logEntityChanges(logConfig));
    return await Promise.all(logPromises);
  } catch (err) {
    console.error('batchLogEntityChanges failed:', {
      action: 'BATCH_LOG_ENTITY_CHANGES',
      error: err.message,
      count: logs.length,
      timestamp: new Date().toISOString()
    });
    return logs.map(() => null);
  }
}

/**
 * Log application error (convenience wrapper for ERROR level)
 */
export async function logError(error, context = {}) {
  await logUserAction({
    action: 'ERROR',
    details: error.message || String(error),
    level: LOG_LEVELS.ERROR,
    ...context
  });
}

/**
 * Log application warning (convenience wrapper for WARN level)
 */
export async function logWarning(message, context = {}) {
  await logUserAction({
    action: 'WARNING',
    details: message,
    level: LOG_LEVELS.WARN,
    ...context
  });
}

/**
 * Log application info (convenience wrapper for INFO level)
 */
export async function logInfo(message, context = {}) {
  await logUserAction({
    action: 'INFO',
    details: message,
    level: LOG_LEVELS.INFO,
    ...context
  });
}

// ============================================================================
// SECURITY LOGGING FUNCTIONS
// ============================================================================

/**
 * Log a security event to the securityLogs collection
 * 
 * @param {Object} options - Security log options
 * @param {string} options.action - Security action (use SECURITY_ACTIONS enum)
 * @param {string} options.actorId - User ID performing the action (null for failed logins)
 * @param {string} options.actorEmail - Email of the user
 * @param {string} options.actorName - Display name of the user
 * @param {string} options.level - Log level (defaults to INFO, use WARN/ERROR for failures)
 * @param {string} options.details - Additional details
 * @param {string} options.ipAddress - IP address (if available)
 * @param {string} options.userAgent - Browser user agent (if available)
 * @param {string} options.failureReason - Reason for failure (for failed logins)
 * @param {Object} options.deletedEntity - Info about deleted entity { entityType, entityId, entityName }
 * @param {Object} options.associations - Related entity IDs for cross-entity queries
 * @returns {Promise<Object|null>} Log document or null on error
 */
export async function logSecurityEvent({
  action,
  actorId = null,
  actorEmail = null,
  actorName = null,
  level = LOG_LEVELS.INFO,
  details = '',
  ipAddress = null,
  userAgent = null,
  failureReason = null,
  deletedEntity = null,
  associations = {}
}) {
  try {
    // Generate human-readable document ID
    const docId = generateSecurityLogId(action, actorId || actorEmail || 'anonymous');

    let logPayload = {
      action,
      actorId,
      actorEmail,
      actorName,
      level,
      details,
      ipAddress,
      userAgent,
      failureReason,
      deletedEntity,
      associations: {
        userId: actorId,
        customerId: associations.customerId || null,
        opportunityId: associations.opportunityId || null,
        taskId: associations.taskId || null,
        contactId: associations.contactId || null,
        orderId: associations.orderId || null
      },
      timestamp: Timestamp.fromDate(new Date())
    };

    // Sanitize sensitive data
    logPayload = sanitizeLogPayload(logPayload);

    // Write to securityLogs collection
    await setDoc(
      docRef(db, collectionPath('securityLogs'), docId),
      { ...logPayload, timestamp: serverTimestamp() }
    );

    return { ...logPayload, id: docId };
  } catch (err) {
    console.error('logSecurityEvent failed:', {
      action: 'LOG_SECURITY_EVENT',
      error: err.message,
      code: err.code,
      securityAction: action,
      timestamp: new Date().toISOString()
    });
    return null;
  }
}

/**
 * Log successful login
 * @param {Object} user - Firebase user object
 * @param {Object} options - Additional options (ipAddress, userAgent)
 */
export async function logLogin(user, options = {}) {
  // Prefer Firestore displayName (passed from AuthContext as userData.displayName || user.displayName).
  // Fall back to the cache, then to the username part of the email.
  const resolvedName = await _resolveDisplayName(user.uid);
  const displayName = user.displayName || resolvedName || user.email?.split('@')[0] || 'User';
  // Warm the cache so subsequent logUserAction calls within the session don't need to fetch.
  if (displayName && user.uid) _displayNameCache.set(user.uid, displayName);
  return logSecurityEvent({
    action: SECURITY_ACTIONS.LOGIN,
    actorId: user.uid,
    actorEmail: user.email,
    actorName: displayName,
    level: LOG_LEVELS.INFO,
    details: `User ${displayName} logged in`,
    ipAddress: options.ipAddress || null,
    userAgent: options.userAgent || (typeof navigator !== 'undefined' ? navigator.userAgent : null)
  });
}

/**
 * Log logout
 * @param {Object} user - Firebase user object
 */
export async function logLogout(user) {
  if (!user) return null;
  const resolvedName = _displayNameCache.get(user.uid) || await _resolveDisplayName(user.uid);
  const displayName = user.displayName || resolvedName || user.email?.split('@')[0] || 'User';
  return logSecurityEvent({
    action: SECURITY_ACTIONS.LOGOUT,
    actorId: user.uid,
    actorEmail: user.email,
    actorName: displayName,
    level: LOG_LEVELS.INFO,
    details: `User ${displayName} logged out`,
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : null
  });
}

/**
 * Log failed login attempt
 * @param {string} email - Email that was attempted
 * @param {string} reason - Reason for failure (e.g., 'Invalid password', 'User not found')
 * @param {Object} options - Additional options (ipAddress, userAgent)
 */
export async function logLoginFailed(email, reason, options = {}) {
  return logSecurityEvent({
    action: SECURITY_ACTIONS.LOGIN_FAILED,
    actorId: null,
    actorEmail: email,
    actorName: null,
    level: LOG_LEVELS.WARN,
    details: `Failed login attempt for ${email}`,
    failureReason: reason,
    ipAddress: options.ipAddress || null,
    userAgent: options.userAgent || (typeof navigator !== 'undefined' ? navigator.userAgent : null)
  });
}

/**
 * Log password reset request
 * @param {string} email - Email requesting reset
 */
export async function logPasswordResetRequest(email) {
  return logSecurityEvent({
    action: SECURITY_ACTIONS.PASSWORD_RESET_REQUEST,
    actorId: null,
    actorEmail: email,
    actorName: null,
    level: LOG_LEVELS.INFO,
    details: `Password reset requested for ${email}`,
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : null
  });
}

/**
 * Log entity deletion (for audit trail)
 * @param {Object} options - Deletion info
 * @param {Object} options.user - User who deleted
 * @param {string} options.entityType - Type of entity deleted
 * @param {string} options.entityId - ID of entity deleted
 * @param {string} options.entityName - Human-readable name of entity
 * @param {Object} options.associations - Related entity IDs
 */
export async function logEntityDeletion({ user, entityType, entityId, entityName, associations = {} }) {
  const resolvedName = user?.uid ? (_displayNameCache.get(user.uid) || await _resolveDisplayName(user.uid)) : null;
  const displayName = user
    ? ((user.firstName && user.lastName) ? `${user.firstName} ${user.lastName}` : (user.displayName || resolvedName || user.email || user.uid))
    : 'Unknown';

  // Auto-populate the deleted entity's own association key
  const entityAssociationMap = {
    Task: 'taskId',
    Opportunity: 'opportunityId',
    Customer: 'customerId',
    Contact: 'contactId',
    Order: 'orderId',
    User: 'userId',
  };
  const assocKey = entityAssociationMap[entityType];
  const mergedAssociations = {
    ...associations,
    userId: user?.uid || null,
    ...(assocKey && !associations[assocKey] ? { [assocKey]: entityId } : {}),
  };

  return logSecurityEvent({
    action: SECURITY_ACTIONS.ENTITY_DELETED,
    actorId: user?.uid || null,
    actorEmail: user?.email || null,
    actorName: displayName,
    level: LOG_LEVELS.WARN,
    details: `Deleted ${entityType}: ${entityName || entityId}`,
    deletedEntity: {
      entityType,
      entityId,
      entityName: entityName || null
    },
    associations: mergedAssociations,
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : null
  });
}

